import type { TransactionResponse } from '../api/payments';

import type { LayoutType } from '../utils/layout';

export type Config = {
  languages: Language[];
  currency: Currency[];
  countries: Country[];
  config: {
    siteConfig: {
      general: {
        logo: string;
        name: string;
      };
      header: {
        search: boolean;
      };
      homePage?: {
        welcomeBanner?:
          | { enabled: false }
          | {
              enabled: true;
              title: string;
              title_signedin: string;
              description: string;
              description_signedin: string;
              image: Record<LayoutType, string>;
            };
        actionButtons?:
          | { enabled: false }
          | { enabled: true; items: [ActionButton, ActionButton] };
        casinoGames?:
          | { enabled: false }
          | { enabled: true; title: string; actionButton: string };
        getStarted?:
          | { enabled: false }
          | {
              enabled: true;
              title: string;
              items: GetStartedItem[];
              actionButton: string;
            };
        widgets?: HomePageWidget[];
      };
      footer: {
        banner: string;
      };
      bottomMenu?: {
        items: BottomMenuItem[];
      };
      cookieModal?: {
        enabled: boolean;
      };
      authForms?: {
        banners?: Partial<
          Record<
            'signin' | 'signup' | 'reset-password',
            Partial<Record<LayoutType, string>>
          >
        >;
      };
      licenses?: {
        list: Array<LicenseConfig>;
      };
      casino: {
        casinoBanner?: SliderBannerConfig;
        liveCasinoBanner?: SliderBannerConfig;
        lastWinners?: LastWinnersConfig;
        buttonIcons?: ButtonIcon[];
        enabled: boolean;
      };
      sportsbook: {
        enabled: boolean;
        banner?: SliderBannerConfig;
      };
      payments?: {
        quickDeposit?: boolean;
        paymentsStaticBlock?: string;
        // server config may return string[] instead of number[]
        depositAmountTemplates?: Record<string, DepositAmountTemplate[]>;
      };
      popups?: {
        defaultDelay: number;
        items: Popup[];
      };
      menu: {
        items: MenuItem[];
      };
      integrations?: {
        gtm?: { enabled: false } | { enabled: true; gtmId: string };
        chat?: ChatConfig;
      };
      responsibleGaming: {
        selfExclusionPeriods: (
          | { enabled: boolean; timeRange: number }
          | { enabled: boolean; forever: true }
        )[];
      };
    };
  };
};

export type DepositAmountTemplate = {
  value: string;
  isDefault: boolean;
};

type ChatsMap = {
  disabled: null;
  livechat: { license: number };
};
export type ChatType = keyof ChatsMap;
export type Chat<T extends ChatType, U = ChatsMap[T]> = {
  type: T;
} & (U extends null ? object : U);
export type ChatConfig = Chat<'disabled'> | Chat<'livechat'>;

export type AnjouanLicenseConfig = {
  name: 'anjouan';
  licenseId: string;
  domId: string;
  enabled: boolean;
};

export type GCLicenseConfig = {
  name: 'gc';
  licenseId: string;
  enabled: boolean;
};

export type APGLicenseConfig = {
  name: 'apg';
  licenseId: string;
  domId: string;
  enabled: boolean;
};

export type XCMLicenseConfig = {
  name: 'xcm';
  licenseId: string;
  domId: string;
  enabled: boolean;
};

export type LicenseConfig =
  | AnjouanLicenseConfig
  | GCLicenseConfig
  | APGLicenseConfig
  | XCMLicenseConfig;

export type Language = {
  name: string;
  code: string;
  isRtl: boolean;
  isDefault: boolean;
  regionId: number;
  hreflang: string;
  alpha2Code: string;
  alpha3Code: string;
};

type Currency = {
  code: string;
  name: string;
};

type Country = {
  name: string;
  code: string;
};

export type BottomMenuItem = {
  id: number;
  label: string;
  icon: string;
} & ({ type: 'link'; location: string } | { type: 'sandwichButton' });

type HomePageWidget = {
  id: number;
  type: 'CTA_BLOCK';
  image: string;
  color: string;
  label?: string;
  headers: string[];
  description?: string;
  button: string;
  link: string;
};

type SliderBannerConfig = {
  group: string;
  delay?: number;
};

type LastWinnersConfig = {
  enabled: boolean;
  delay?: number;
};

type ButtonIcon = {
  type: ButtonIconTypes;
} & ({ useDefaultIcon: true } | { useDefaultIcon: false; icon: string });

export enum ButtonIconTypes {
  LOBBY = 'lobby',
  ALL_GAMES = 'all_games',
  FAVOURITES = 'favourites',
  LIVE_CASINO = 'live_casino',
}

export type PaymentPopupDataType = TransactionResponse & {
  imageSources: Record<PropertyKey, string | undefined>;
};

export type RealityCheckPopupDataType = {
  period?: string;
};

export type Popup = {
  type: 'default' | 'non-blocking';
  closeByBackdrop?: boolean;
  delay?: number;
  immediate?: true;
} & (
  | {
      content: string;
      contentType?: never;
      data?: never;
    }
  | {
      contentType: 'logout';
      content?: never;
      data?: never;
    }
  | {
      contentType: 'auth';
      content?: never;
      data?: never;
    }
  | {
      contentType: 'deposit' | 'withdrawal';
      content?: never;
      data: PaymentPopupDataType;
    }
  | {
      contentType: 'quickDeposit';
      content?: never;
      data?: never;
    }
  | {
      contentType: 'realityCheck';
      content?: never;
      data: RealityCheckPopupDataType;
    }
  | {
      contentType: 'multipurpose';
      content?: never;
      data: {
        enabled: boolean;
        trigger: PopupTrigger;
        displayTimes?: number;
        image: string;
        staticBlock: string;
        link: string;
        target: LinkTarget;
      };
    }
  | {
      contentType: 'cookie';
      data?: never;
      content?: never;
    }
);

export type MenuItem = {
  label: string;
  location: string;
  icon?: string;
};

export type ActionButton = {
  image: string;
  color: string;
  label: string;
  link: string;
};

export type GetStartedItem = {
  label: string;
  description: string;
  icon?: string;
};

export enum LinkTarget {
  Blank = '_blank',
  Top = '_top',
}

export enum PopupTrigger {
  SiteOpening = 'site-opening',
  SingIn = 'sign-in',
  SignUp = 'sign-up',
}

export type GetFieldType<Obj, Path> =
  Path extends `${infer Left}.${infer Right}`
    ? Left extends keyof Obj
      ?
          | GetFieldType<Exclude<Obj[Left], undefined>, Right>
          | Extract<Obj[Left], undefined>
      : undefined
    : Path extends keyof Obj
      ? Obj[Path]
      : undefined;
